// buildVerifyTask.js — the per-task DAG verifier, extracted from SessionWorkspace (audit: god-component).
// @arch:pure-engine
//
// This was a ~50-line closure inside the 2259-line SessionWorkspace with ZERO unit coverage despite
// being the most LAW-sensitive logic in the renderer (it decides each DAG task's honest-green verdict).
// Extracting it to a PURE factory (all runtime deps injected) is behavior-identical AND finally makes
// it testable in isolation. The 3 verdict semantics (verified-green / unverified / red) come from the
// pure verdictClass helper; the node --check floor + held-out judge from syntaxFloor.
//
// THE LAW: the oracle/replay path is still the SOLE judge of DONE. No score, no confidence, no model
// self-grading. 'unverified' (no executable verifier matched, no-silent-green ON) completes the task
// but is LABELED honestly — never recorded as a verified green.
//
// PURE engine (Tier 1): no react/window/IPC. runOracle, freshVerify, observe, runCommand are injected.

import { classifyVerdict } from './verdictClass.js';
import { buildSyntaxFloorVerifier, listChangedJsFiles } from './syntaxFloor.js';

/**
 * @param {object} deps
 * @param {Function} deps.verifiersForTask    ({task,root,runCommand,scripts,previewConsole,buildDefaultVerifiers})→map
 * @param {string}   deps.root
 * @param {Function} deps.runCommand          shell port ({command,cwd})→{exitCode,stdout,stderr}
 * @param {object}   [deps.scripts]
 * @param {Function} [deps.previewConsole]
 * @param {Function} deps.buildDefaultVerifiers
 * @param {Function} deps.runOracle           ({verifiers,taskText,signal})→{reachedGreen,noVerifiers,verdicts}
 * @param {Function} deps.freshVerify         A3 fresh/held-out judge; returns null when disabled
 * @param {any}      [deps.signal]            AbortSignal threaded to the oracle
 * @param {Function} [deps.observe]           best-effort event sink ('dag_task_verdict', payload)
 * @param {boolean}  [deps.noSilentGreen]     M1/M2 on?
 * @param {boolean}  [deps.freshOn]           A3 on? (build the held-out floor)
 * @param {Function} [deps.onVerifierRan]     called when a REAL verifier ran (sets dagAnyVerifier)
 * @returns {(task:object)=>Promise<{green:boolean, verified?:boolean, evidence:any}>}
 */
export function buildVerifyTask({
  verifiersForTask, root, runCommand, scripts, previewConsole, buildDefaultVerifiers,
  runOracle, freshVerify, signal, observe = () => {},
  noSilentGreen = false, freshOn = false, onVerifierRan = () => {},
} = {}) {
  return async function verifyTask(task) {
    let verifiers = verifiersForTask({ task, root, runCommand, scripts, previewConsole, buildDefaultVerifiers });
    // Compute the task's changed .js files at most ONCE per call (the M2 floor and the A3 held-out
    // floor both need it — avoid a redundant `git diff`).
    let changedJs;
    const getChangedJs = async () => {
      if (changedJs === undefined) changedJs = await listChangedJsFiles({ root, runCommand });
      return changedJs;
    };
    // no-silent-green: when ON and no project verifier matched, try a REAL node --check floor on this
    // task's changed .js files (M2); if still unverifiable, LABEL it 'unverified' (M1) — never silent
    // green. Flag OFF ⇒ the original early-return, byte-identical to today.
    if (!verifiers || Object.keys(verifiers).length === 0) {
      if (noSilentGreen) {
        const floor = buildSyntaxFloorVerifier({ touchedFiles: await getChangedJs(), root, runCommand });
        if (floor) {
          verifiers = { syntax: floor }; // a real executable verifier — fall through to the oracle
        } else {
          try {
            observe('dag_task_verdict', { taskId: task.id, green: true, verified: false, verdictClass: 'unverified', verifiers: [], surface: 'code' });
          } catch { /* best-effort */ }
          return { green: true, verified: false, evidence: 'no verifier (honest no-op — unverified)' };
        }
      } else {
        return { green: true, evidence: 'no verifier (honest no-op)' };
      }
    }
    // A3 — fresh-context + HELD-OUT (agent-invisible syntax floor) judge when enabled; null ⇒ inline oracle.
    let heldOutVerifiers;
    if (freshOn) {
      const floor = buildSyntaxFloorVerifier({ touchedFiles: await getChangedJs(), root, runCommand });
      if (floor) heldOutVerifiers = { syntax: floor };
    }
    const fresh = await freshVerify({ verifiers, heldOutVerifiers, taskText: task.name || task.id, signal });
    const o = fresh
      ? { reachedGreen: fresh.green, noVerifiers: fresh.noVerifiers, verdicts: fresh.verdicts }
      : await runOracle({ verifiers, taskText: task.name || task.id, signal });
    if (!o.noVerifiers) onVerifierRan();
    try {
      observe('dag_task_verdict', {
        taskId: task.id,
        green: !!(o.reachedGreen || o.noVerifiers),
        ...(noSilentGreen ? { verified: classifyVerdict(o) === 'green', verdictClass: classifyVerdict(o) } : {}),
        verifiers: (o.verdicts || []).map((v) => v.verifier),
        surface: 'code',
      });
    } catch { /* observe is best-effort */ }
    return { green: o.reachedGreen || o.noVerifiers, ...(noSilentGreen ? { verified: classifyVerdict(o) === 'green' } : {}), evidence: o.verdicts };
  };
}

export default buildVerifyTask;
